import type { AutocompleteProps } from "@mui/material";
import { Grid } from "@mui/material";
import { debounce } from "lodash";
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAsyncEffect from "use-async-effect";
import { refDataApi } from "../../../api/refdata";
import { useSelector } from "../../../store";
import { isTypeCodeableConcept } from "../../../types/codeableConcept";
import { isAbortError } from "../../../utils/isAbortError";
import MultipleInput from "./components/MultipleInput";
import SingleInput from "./components/SingleInput";
import { useTypeAheadInput } from "./hooks/useTypeAheadInput";
import { localOptions } from "./localOptions";
import { normalizeTypeaheadSearchQuery } from "./typeaheadFilterOptions";
import { TYPEAHEAD_BROWSE_LIMITS } from "./typeaheadBrowseLimits";
import {
  resolveTypeaheadSectionLabels,
  TypeaheadOptionSectionLabels,
} from "./typeaheadOptionSections";

export interface TypeAheadProps
  extends Partial<
    AutocompleteProps<
      any,
      boolean | undefined,
      boolean | undefined,
      boolean | undefined
    >
  > {
  fieldLabel: string;
  disabled?: boolean;
  isMultiple?: boolean;
  freeOptions?: boolean;
  onChange: (value: any, reason?: string) => void;
  getValueLabel: (a: any) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  getOptionLabel?: (a: any) => string;
  getOptionValue?: (a: any) => void; // only used for Snomed inputs
  optionType: string;
  value: any;
  subset?: any;
  required?: boolean;
  size?: "small" | "medium";
  variant?: "standard" | "outlined" | "filled";
  disableCloseOnSelect?: boolean;
  /** Optional overrides for grouped dropdown section headers when `freeOptions` is enabled. */
  optionSectionLabels?: TypeaheadOptionSectionLabels;
}

function useStoredOptions(optionType) {
  return useSelector((state) =>
    state.options[optionType] ? state.options[optionType] : [],
  );
}

const TypeAheadInput: FC<TypeAheadProps> = ({
  fieldLabel,
  disabled = false,
  isMultiple = false,
  freeOptions = false,
  getValueLabel,
  getOptionLabel,
  isOptionEqualToValue,
  getOptionValue,
  onChange,
  required = false,
  value,
  optionType,
  size,
  variant,
  subset,
  disableCloseOnSelect = false,
  optionSectionLabels,
}) => {
  const storedOptions = useStoredOptions(optionType);

  const { storeInitialValue } = useTypeAheadInput();

  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState(storedOptions);
  const [loading, setLoading] = useState(localOptions[optionType] !== true);
  const [searchString, setSearchString] = useState<string>(inputValue);
  const [valueUpdateCounter, setValueUpdateCounter] = useState<number>(0);

  const [otherInitialCheck, setOtherInitialCheck] = useState<boolean>(false);
  const searchRequestRef = useRef(0);
  const searchAbortRef = useRef<AbortController | null>(null);

  useLayoutEffect(() => {
    if (!otherInitialCheck) determineIfValueIsTypeOther(value);
  }, [value]);

  const determineIfValueIsTypeOther = (value) => {
    const SECOND_VALUE_UPDATE_HAS_HAPPENED = valueUpdateCounter > 1;
    if (value) {
      if (Array.isArray(value) && value.length === 1) {
        const IS_CODEABLE_CONCEPT = isTypeCodeableConcept(value[0]);
        const isOther = IS_CODEABLE_CONCEPT
          ? codeableConceptAsOtherCheck(value[0])
          : codingAsOtherCheck(value[0]);

        if (isOther) {
          setOtherInitialCheck(true);
          setInputAndSearch(
            value[0].coding[0].display,
            IS_CODEABLE_CONCEPT ? value[0].coding[0].display : value[0].code,
          );
        }
      }

      if (!Array.isArray(value)) {
        const isOther =
          value?.code && typeof value.code === "string"
            ? value.code.startsWith("other")
            : isTypeCodeableConcept(value)
              ? codeableConceptAsOtherCheck(value)
              : false;

        if (isOther) {
          const otherValue = value?.display ?? value?.coding[0]?.display;
          setOtherInitialCheck(true);
          setInputAndSearch(otherValue, false);
        }
      }
    }

    if (SECOND_VALUE_UPDATE_HAS_HAPPENED) {
      setOtherInitialCheck(true);
    }
    setValueUpdateCounter(valueUpdateCounter + 1);
  };

  const codeableConceptAsOtherCheck = (value) =>
    value && value.coding[0].code && typeof value.coding[0].code === "string"
      ? value.coding[0].code.startsWith("other")
      : false;

  const codingAsOtherCheck = (value) =>
    value && value.code && typeof value.code === "string"
      ? value.code.startsWith("other")
      : false;

  const isValueTypeOther = (value) => {
    try {
      if (!value || !value?.code) return false;

      if (Array.isArray(value) && value.length === 1) {
        return `${value[0].coding[0].code}`.startsWith("other");
      }

      if (!Array.isArray(value)) {
        return value?.code && typeof value.code === "string"
          ? `${value.code}`.startsWith("other")
          : false;
      }

      return false;
    } catch (e) {
      console.error(value);
    }
  };

  const setInputAndSearch = (value, allowSearch: boolean = true) => {
    setInputValue(value);
    if (allowSearch) {
      setSearchString(value);
    }
  };

  const fetchRemoteOptionsRef = useRef<(input: string) => Promise<void>>();
  fetchRemoteOptionsRef.current = async (input) => {
    const query = normalizeTypeaheadSearchQuery(input);
    const requestId = ++searchRequestRef.current;

    searchAbortRef.current?.abort();
    const abortController = new AbortController();
    searchAbortRef.current = abortController;

    try {
      setLoading(true);
      const isBrowse = !query;
      const results = await refDataApi.searchOptions(
        optionType,
        query,
        subset,
        abortController.signal,
        isBrowse ? TYPEAHEAD_BROWSE_LIMITS.SYSTEM_OPTIONS : undefined,
      );
      if (requestId === searchRequestRef.current) {
        setOptions(results ?? []);
      }
    } catch (e) {
      if (isAbortError(e)) return;
      console.error(e);
      if (requestId === searchRequestRef.current) {
        setOptions([]);
      }
    } finally {
      if (requestId === searchRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchBrowseOptions = useCallback(() => {
    if (localOptions[optionType]) return;
    fetchRemoteOptionsRef.current?.("");
  }, [optionType]);

  const handleDropdownOpen = useCallback(() => {
    if (localOptions[optionType]) return;
    if (!normalizeTypeaheadSearchQuery(searchString)) {
      fetchBrowseOptions();
    }
  }, [optionType, searchString, fetchBrowseOptions]);

  const debouncedSearch = useCallback(
    debounce((input: string) => fetchRemoteOptionsRef.current?.(input), 200),
    [],
  );

  useEffect(
    () => () => {
      debouncedSearch.cancel();
      searchAbortRef.current?.abort();
    },
    [debouncedSearch],
  );

  useAsyncEffect(() => {
    if (localOptions[optionType]) return;

    debouncedSearch.cancel();
    const query = normalizeTypeaheadSearchQuery(searchString);

    if (!query) {
      fetchBrowseOptions();
      return;
    }

    setOptions([]);
    debouncedSearch(query);
  }, [searchString, optionType, fetchBrowseOptions, debouncedSearch]);

  const onItemSelection = (val, reason) => {
    if (val) {
      if (!isMultiple) {
        // this is single section
        onChange(getOptionValue ? getOptionValue(val) : val);
      } else {
        // this is multiple section
        const v = getOptionValue ? val.map((v) => getOptionValue(v)) : val;
        onChange(v, reason);
        debouncedSearch.cancel();
        setOptions([]);
        setLoading(false);
      }
    } else {
      onChange(undefined, reason);
      debouncedSearch.cancel();
      setOptions([]);
      setLoading(false);
    }
  };

  const isRemoteSearch = !localOptions[optionType];

  const sectionLabels = useMemo(
    () =>
      freeOptions
        ? resolveTypeaheadSectionLabels(fieldLabel, optionSectionLabels)
        : undefined,
    [freeOptions, fieldLabel, optionSectionLabels],
  );

  return (
    <Grid item xs={12}>
      <Grid container alignContent="center" alignItems="center" direction="row">
        <Grid item xs={12}>
          {isMultiple ? (
            <MultipleInput
              value={isValueTypeOther(value) ? undefined : value}
              options={options}
              freeOptions={freeOptions}
              disabled={disabled}
              loading={loading}
              onItemSelection={onItemSelection}
              getOptionValue={getOptionValue}
              setInputValue={setInputAndSearch}
              required={required}
              optionType={optionType}
              subset={subset}
              fieldLabel={fieldLabel}
              size={size}
              variant={variant}
              isOptionEqualToValue={isOptionEqualToValue}
              storeInitialValue={storeInitialValue}
              disableCloseOnSelect={disableCloseOnSelect}
              getOptionLabel={getOptionLabel}
              getValueLabel={getValueLabel}
              isRemoteSearch={isRemoteSearch}
              searchQuery={searchString}
              onDropdownOpen={handleDropdownOpen}
              sectionLabels={sectionLabels}
            />
          ) : (
            <SingleInput
              value={value ?? ""}
              options={options}
              freeOptions={freeOptions}
              disabled={disabled}
              loading={loading}
              onItemSelection={onItemSelection}
              getOptionValue={getOptionValue}
              setInputValue={setInputAndSearch}
              required={required}
              optionType={optionType}
              subset={subset}
              fieldLabel={fieldLabel}
              size={size}
              variant={variant}
              isOptionEqualToValue={isOptionEqualToValue}
              disableCloseOnSelect={disableCloseOnSelect}
              getOptionLabel={getOptionLabel}
              isRemoteSearch={isRemoteSearch}
              searchQuery={searchString}
              onDropdownOpen={handleDropdownOpen}
              sectionLabels={sectionLabels}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TypeAheadInput;
